import API_URL from '../config';
import { getAuthHeaders } from '../utils/api';
import { getLocalDateString } from '../utils/formatters';

// ==================== CATEGORIES ====================

export const getCategories = async () => {
  const response = await fetch(`${API_URL}/api/admin/categories`, {
    headers: getAuthHeaders({ json: true }),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch categories');
  }

  return response.json();
};

// ==================== IMAGE LIBRARY ====================

export const uploadAdminImage = async (
  imageData,
  title,
  description = '',
  defaultConfig = {},
  scheduleDate = null,
  categories = [],
) => {
  const response = await fetch(`${API_URL}/api/admin/images`, {
    method: 'POST',
    headers: getAuthHeaders({ json: true }),
    body: JSON.stringify({
      imageData,
      title,
      description,
      defaultConfig: {
        rows: defaultConfig.rows || 4,
        columns: defaultConfig.columns || 4,
        pieceStyle: defaultConfig.pieceStyle || 'classic',
      },
      scheduleDate,
      categories,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to upload image');
  }

  return response.json();
};

export const getAdminImages = async () => {
  const response = await fetch(`${API_URL}/api/admin/images`, {
    headers: getAuthHeaders({ json: true }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch images');
  }

  return response.json();
};

export const getAdminImage = async (imageId) => {
  const response = await fetch(`${API_URL}/api/admin/images/${imageId}`, {
    headers: getAuthHeaders({ json: true }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch image');
  }

  return response.json();
};

export const updateAdminImage = async (imageId, updates) => {
  const response = await fetch(`${API_URL}/api/admin/images/${imageId}`, {
    method: 'PATCH',
    headers: getAuthHeaders({ json: true }),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update image');
  }

  return response.json();
};

export const deleteAdminImage = async (imageId) => {
  const response = await fetch(`${API_URL}/api/admin/images/${imageId}`, {
    method: 'DELETE',
    headers: getAuthHeaders({ json: true }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete image');
  }

  return response.json();
};

// ==================== DAILY PUZZLES ====================

export const scheduleDailyPuzzle = async (date, adminImageId, puzzleConfig = {}) => {
  const response = await fetch(`${API_URL}/api/admin/daily-puzzles`, {
    method: 'POST',
    headers: getAuthHeaders({ json: true }),
    body: JSON.stringify({
      date,
      adminImageId,
      rows: puzzleConfig.rows || 4,
      columns: puzzleConfig.columns || 4,
      pieceStyle: puzzleConfig.pieceStyle || 'classic',
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to schedule daily puzzle');
  }

  return response.json();
};

export const getDailyPuzzles = async () => {
  const response = await fetch(`${API_URL}/api/admin/daily-puzzles`, {
    headers: getAuthHeaders({ json: true }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch daily puzzles');
  }

  return response.json();
};

export const updateDailyPuzzle = async (puzzleId, updates) => {
  const response = await fetch(`${API_URL}/api/admin/daily-puzzles/${puzzleId}`, {
    method: 'PATCH',
    headers: getAuthHeaders({ json: true }),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update daily puzzle');
  }

  return response.json();
};

export const deleteDailyPuzzle = async (puzzleId) => {
  const response = await fetch(`${API_URL}/api/admin/daily-puzzles/${puzzleId}`, {
    method: 'DELETE',
    headers: getAuthHeaders({ json: true }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete daily puzzle');
  }

  return response.json();
};

// ==================== ANALYTICS / STATS ====================

export const getStats = async () => {
  const localDate = getLocalDateString();
  const response = await fetch(`${API_URL}/api/admin/stats?date=${localDate}`, {
    headers: getAuthHeaders({ json: true }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch statistics');
  }

  return response.json();
};

export const getPuzzleRatings = async (puzzleId) => {
  const response = await fetch(`${API_URL}/api/admin/puzzle-ratings/${puzzleId}`, {
    headers: getAuthHeaders({ json: true }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch puzzle ratings');
  }

  return response.json();
};

export const getImageRatings = async (imageId) => {
  const response = await fetch(`${API_URL}/api/admin/image-ratings/${imageId}`, {
    headers: getAuthHeaders({ json: true }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch image ratings');
  }

  return response.json();
};

// ==================== FEEDBACK MANAGEMENT ====================

export const getFeedback = async () => {
  const response = await fetch(`${API_URL}/api/admin/feedback`, {
    headers: getAuthHeaders({ json: true }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch feedback');
  }

  return response.json();
};

export const updateFeedbackStatus = async (feedbackId, status) => {
  const response = await fetch(`${API_URL}/api/admin/feedback/${feedbackId}`, {
    method: 'PATCH',
    headers: getAuthHeaders({ json: true }),
    body: JSON.stringify({ status }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update feedback');
  }

  return response.json();
};

export const deleteFeedback = async (feedbackId) => {
  const response = await fetch(`${API_URL}/api/admin/feedback/${feedbackId}`, {
    method: 'DELETE',
    headers: getAuthHeaders({ json: true }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete feedback');
  }

  return response.json();
};
